import { type FC, useEffect, useRef, useState } from 'react';
import Carousel from 'react-multi-carousel';
import { useNavigate } from 'react-router';
import 'react-multi-carousel/lib/styles.css';
import { Avatar, Grid2, Typography, useTheme } from '@mui/material';
import type { UnresolvedFaultsDto } from '../../models';
import { AvaniTempCard } from '../Cards';

type AvaniFaultsMessageProps = {
  faults: UnresolvedFaultsDto[];
};

const CARD_WIDTH = 220;
const CARD_GAP = 12;

export const AvaniFaultsMessage: FC<AvaniFaultsMessageProps> = ({ faults }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const [itemsToShow, setItemsToShow] = useState(1);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new ResizeObserver(([entry]) => {
      const containerWidth = entry.contentRect.width;
      const fullCardWidth = CARD_WIDTH + CARD_GAP;
      const count = containerWidth / fullCardWidth;
      setItemsToShow(count);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const responsiveCarouselConfig = {
    desktop: {
      breakpoint: { max: 4000, min: 0 },
      items: itemsToShow,
      partialVisibilityGutter: CARD_GAP
    }
  };

  return (
    <Grid2
      ref={containerRef}
      container
      columns={10}
      spacing={2}
      sx={{ [`.carousel-container`]: { display: 'flex' } }}
      alignItems="center"
    >
      <Grid2 container direction="row" spacing={1} size={{ xs: 10 }} alignItems="center" padding="10px">
        <Avatar sx={{ backgroundColor: theme.colors.avani.magenta }}>
          <Typography fontSize={18} sx={{ color: 'white', marginTop: '3px' }}>
            {faults.length}
          </Typography>
        </Avatar>
        <Grid2>
          <Typography variant={'h3'} sx={{ color: 'white' }}>
            Faults
          </Typography>
        </Grid2>
        <Grid2>
          <Typography variant={'h3'} color={theme.colors.avani.lightMagenta}>
            Unresolved
          </Typography>
        </Grid2>
      </Grid2>
      <Grid2 size={{ xs: 10 }}>
        <Carousel
          swipeable
          draggable
          arrows
          responsive={responsiveCarouselConfig}
          autoPlaySpeed={3000}
          keyBoardControl={true}
          transitionDuration={1000}
          shouldResetAutoplay
          containerClass="carousel-container"
          dotListClass="custom-dot-list-style"
          itemClass="carousel-item-padding-40-px"
        >
          {faults.map(({ id, name, problem }, ind) => {
            return (
              <AvaniTempCard
                id={ind}
                key={ind}
                noPopover={true}
                temp={{
                  title: name ?? '',
                  value: false,
                  isOor: true,
                  desc: problem ?? ''
                }}
                sx={{
                  marginLeft: '8px',
                  marginRight: '8px',
                  width: CARD_WIDTH,
                  cursor: 'pointer'
                }}
                onClick={() => navigate(`/dashboard/faults/${id}`)}
              />
            );
          })}
        </Carousel>
      </Grid2>
    </Grid2>
  );
};
